import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useMainStore } from '@/modules/shared/stores/main.store'
import { formatDateBy } from '@/modules/shared/utils/dateFormatter'
import { removerAcentos } from '@/modules/shared/utils/remover-acento'

import { useCustomerFormData } from '../data/customer-form.data';
import { formatCepToFillTheField, formatCnpjCpfHelper, formatPhoneHelper, formatPhoneToFillTheField } from '../helpers/format-fields-customer-form.helper';
import { useCustomerFormStore, useCustomerFormStoreAction } from '../stores/customer-form.store';
import { customerFormValidation, type TCustomerFormValidation } from '../validation/client-form.validation';
import { useClearCustomerStore } from './use-clear-store.hook';
import { useStartOrderByCustomer } from './use-start-order-by-customer.hook';
import { useCustomerFormLayout } from './use-customer-form-layout.hook';
import { set } from 'lodash'

export const useCustomerForm = () => {
  const [isSearchingCep, setIsSearchingCep] = useState(false)
  const [isLoadingCnpj, setIsLoadingCnpj] = useState(false)
  const [isSubmiting, setIsSubmiting] = useState(false)
  const navigate = useNavigate()

  const form = useForm<TCustomerFormValidation>({
    resolver: customerFormValidation,
  })

  const { control, setValue, watch, setFocus } = form

  const startOrderOfClientHook = useStartOrderByCustomer()
  const customerFormData = useCustomerFormData()
  const clearStoreHook = useClearCustomerStore()

  // Integração do sistema de layout
  const {
    customerFormLayout,
    isLoadingLayout,
    isFieldVisible,
    isFieldEnabled,
    getFieldLabel,
    getFieldMaxLength
  } = useCustomerFormLayout()

  const { setIsLoading } = useMainStore().actions

  const {
    operationType,
    customerFormJson,
    customerFormTable,
    customerFormCnpj,
    customerFormCities,
    customerFormZipCode,
    operationToFillValues,
    isFillValuesAfterRequestCities,
  } = useCustomerFormStore()

  const {
    setIsSaveForm,
    setOperationToFillValues,
    setCnpjOfClientToDataSearch,
    setIsFillValuesAfterRequestCities,
  } = useCustomerFormStoreAction

  const handleBackPage = () => {
    navigate('/clientes')
    clearStoreHook.action.clearAllStore()
  }

  const fillValuesInFieldsOfCustomerForm = () => {
    const indexState = customerFormTable?.listaUF?.findIndex(
      (item) => item.idUf === Number(customerFormJson?.fkUF),
    )

    setValue('codigo', customerFormJson?.idEntidade)
    setValue('cnpjCpf', formatCnpjCpfHelper(customerFormJson?.cnpj_cpf))
    setValue('nomeCliente', customerFormJson?.razaoSocial_Nome)
    setValue('frete', customerFormJson?.fkFrete)
    setValue('faturamento', customerFormJson?.fkTipoFaturamento)
    setValue('tabelaPreco', customerFormJson?.fkTabelaPreco)
    setValue('grupo', customerFormJson?.fkGrupoEntidade)
    setValue('simplesNacional', customerFormJson?.flagSimplesNacional)
    setValue('contribuinteIcms', customerFormJson?.flagContribuinteIcms)
    setValue('consumidorFinal', customerFormJson?.flagConsumidorFinal)
    setValue('aplicaSt', customerFormJson?.flagAplicaSt)
    setValue('suframa', customerFormJson?.suframa)
    setValue(
      'ultimaCompra',
      customerFormJson?.ultimaCompra &&
        formatDateBy(customerFormJson?.ultimaCompra, 'DD/MM/YY'),
    )
    setValue('cepEndereco', formatCepToFillTheField(customerFormJson?.cep))
    setValue('logradouro', customerFormJson?.logradouro)
    setValue('numero', customerFormJson?.numero)
    setValue('complemento', customerFormJson?.complemento)
    setValue('bairro', customerFormJson?.bairro)
    setValue(
      'estado',
      customerFormJson?.fkUF
        ? String(customerFormTable?.listaUF[indexState]?.idUf)
        : String(customerFormTable?.listaUF[0]?.idUf),
    )
    setValue(
      'cidade',
      customerFormJson?.fkCidade
        ? customerFormJson?.fkCidade
        : String(customerFormCities[0]?.id),
    )
    setValue('contato', customerFormJson?.contato)
    setValue('telefone', formatPhoneToFillTheField(customerFormJson?.fone))
    setValue('email', customerFormJson?.email)

    setIsFillValuesAfterRequestCities(false)
    setOperationToFillValues('customerForm')
  }

  const fillValuesInFieldsOfCustomerFormCnpj = () => {
    setValue('nomeCliente', customerFormCnpj?.razao_social)
    setValue('simplesNacional', customerFormCnpj?.opcao_pelo_simples !== null)
    setValue('cepEndereco', formatCepToFillTheField(customerFormCnpj?.cep))
    setValue('logradouro', customerFormCnpj?.logradouro)
    setValue('numero', customerFormCnpj?.numero)
    setValue('complemento', customerFormCnpj?.complemento)
    setValue('bairro', customerFormCnpj?.bairro)
    setValue(
      'estado',
      customerFormCnpj?.codigo_municipio_ibge?.toString().slice(0, 2),
    )
    setValue('cidade', String(customerFormCnpj?.codigo_municipio_ibge))
    setValue('contato', customerFormCnpj?.razao_social)
    setValue(
      'telefone',
      formatPhoneToFillTheField(customerFormCnpj?.ddd_telefone_1),
    )
    setValue('email', customerFormCnpj?.email)

    setIsFillValuesAfterRequestCities(false)
    setOperationToFillValues('customerForm')
  }

  const fillValuesInFieldsOfCustomerFormZipCode = () => {
    setValue('logradouro', customerFormZipCode?.logradouro)
    setValue('complemento', customerFormZipCode?.complemento)
    setValue('bairro', customerFormZipCode?.bairro)
    setValue('estado', customerFormZipCode?.ibge?.slice(0, 2))
    setValue('cidade', String(customerFormZipCode?.ibge))

    setIsFillValuesAfterRequestCities(false)
    setOperationToFillValues('customerForm')
  }

  const handleChangeStateAndLoadCity = (ufId: string) => {
    setOperationToFillValues('changeState')
    customerFormData.action.onSubmitCustomerFormCities(ufId)
    setIsFillValuesAfterRequestCities(false)
  }

  const checkIfDataOfCnpjOrCpfIsValid = async () => {
    setIsLoadingCnpj(true)
    const cnpjOrCpf = watch('cnpjCpf')

    if (cnpjOrCpf === '') {
      setIsLoadingCnpj(false)
      return
    }

    const valueFormatted = cnpjOrCpf.replace(/(\D)/g, '')

    setCnpjOfClientToDataSearch(valueFormatted)

    await customerFormData.action.onSubmitCustomerFormValidCnpjOrCpf({
      payload: { cnpjOrCpf: valueFormatted },
    })
    setIsLoadingCnpj(false)
  }

  const handleSearchZipCode = async () => {
    try {
      setIsSearchingCep(true)
      if (operationType === 'view') return
      const valueFormatted = watch('cepEndereco').replace(/(\D)/g, '')

      if (valueFormatted.length !== 8) {
        toast.info('CEP Invalido')
        setFocus('cepEndereco')
        setIsSearchingCep(false)
        return
      }

      setOperationToFillValues('clientCep')
      await customerFormData.action.onSubmitCustomerFormZipCode({
        payload: { zipCode: valueFormatted },
      })
      setIsFillValuesAfterRequestCities(false)
      setIsSearchingCep(false)
    } catch (error) {
      setIsSearchingCep(false)
    }
  }

  const setValueInFieldOfCity = () => {
    setValue('cidade', String(customerFormCities[0]?.id) || '')

    setIsFillValuesAfterRequestCities(false)
    setOperationToFillValues('customerForm')
  }

  const handleSubmitForm = async (data: TCustomerFormValidation) => {
    setIsSubmiting(true)
    if (data.telefone !== '') {
      const regex = /(\(\d{2}\)\s)(\d{4,5}-\d{4})/;
      if (!regex.test(data.telefone)) {
        toast.info('Verifique o número de telefone digitado')
        setFocus('telefone')
        return
      }
    }

    fillJsonWithValues(data)
    setIsSaveForm(true)

    if (operationType !== 'add') {
      await customerFormData.action.onSubmitCustomerFormSaveAction()
      return
    }

    await customerFormData.action.onSubmitCustomerFormValidCnpjOrCpf({
      payload: {
        cnpjOrCpf: data.cnpjCpf.replace(/\D/g, ''),
      },
    })

    setIsSubmiting(false)
  }

  const fillJsonWithValues = (data: TCustomerFormValidation) => {
    customerFormJson.bairro = removerAcentos(data.bairro)
    customerFormJson.cep = data.cepEndereco.replace(/\D/g, '')
    customerFormJson.cnpj_cpf = data.cnpjCpf.replace(/\D/g, '')
    customerFormJson.complemento = removerAcentos(data.complemento)
    customerFormJson.contato = removerAcentos(data.contato)
    customerFormJson.email = removerAcentos(data.email)
    customerFormJson.fkFrete = data.frete
    customerFormJson.fkGrupoEntidade = data.grupo
    customerFormJson.fkTabelaPreco = data.tabelaPreco
    customerFormJson.fkTipoFaturamento = data.faturamento
    customerFormJson.fkCidade = data.cidade
    customerFormJson.flagAplicaSt = data.aplicaSt
    customerFormJson.flagConsumidorFinal = data.consumidorFinal
    customerFormJson.flagContribuinteIcms = data.contribuinteIcms
    customerFormJson.flagSimplesNacional = data.simplesNacional
    customerFormJson.fone = data.telefone.replace(
      /(\d{2})(\d{4,5})(\d{4})/,
      '($1) $2-$3',
    )
    customerFormJson.logradouro = removerAcentos(data.logradouro)
    customerFormJson.numero = data.numero
    customerFormJson.razaoSocial_Nome = removerAcentos(data.nomeCliente)
  }

  const handleStartOrder = () => {
    startOrderOfClientHook.action.handleStartOrder()
  }

  useEffect(() => {
    if (customerFormJson === undefined) {
      customerFormData.action.onSubmitCustomerFormTablesParams()
    }

    return () => {
      if (
        customerFormJson !== undefined &&
        operationToFillValues === 'customerForm'
      ) {
        setIsFillValuesAfterRequestCities(true)
        return
      }

      setOperationToFillValues('customerForm')
    }

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (isFillValuesAfterRequestCities) {
      switch (operationToFillValues) {
        case 'customerForm':
          return fillValuesInFieldsOfCustomerForm()
        case 'clientCnpj':
          return fillValuesInFieldsOfCustomerFormCnpj()
        case 'clientCep':
          return fillValuesInFieldsOfCustomerFormZipCode()
        default:
          return setValueInFieldOfCity()
      }
    }

    return undefined
    // eslint-disable-next-line
  }, [isFillValuesAfterRequestCities])

  useEffect(() => {
    if (
      customerFormData.isLoadingCustomerFormData ||
      isLoadingLayout ||
      !customerFormJson?.idEntidade ||
      !customerFormTable
    ) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [
    customerFormData.isLoadingCustomerFormData,
    isLoadingLayout,
    customerFormJson?.idEntidade,
    customerFormTable,
  ])

  return {
    form,
    operationType,
    customerFormJson,
    customerFormTable,
    customerFormCities,
    customerFormLayout,
    isLoading: customerFormData.isLoadingCustomerFormData || isLoadingLayout,
    isEditClient: operationType === 'edit',
    isViewClient: operationType === 'view',
    isSearchingCep,
    isLoadingCnpj,
    isSubmiting,
    inputControl: control,
    // Layout functions
    isFieldVisible,
    isFieldEnabled,
    getFieldLabel,
    getFieldMaxLength,
    // Action functions
    handleBackPage,
    handleStartOrder,
    handleSubmitForm,
    handleSearchZipCode,
    handleChangeStateAndLoadCity,
    checkIfDataOfCnpjOrCpfIsValid,
  }
}